import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import breakpoint from 'tools/styled-components-breakpoint'
import Component from 'decorators/Component'
import Flex from 'components/Flex'
import { P } from 'components/Typo'
import { padding, paddingSizes, margin, marginSizes, colors } from 'theme/default'
import { getTranslatedMonths } from 'tools/date'

const CaptionContainer = styled.div`
  display: table-caption;
  text-align: center;
  text-transform: uppercase;
  height: 2.5em;
  ${breakpoint('desktop')`
    height: 3.5em;
  `}
`

const Caption = ({ date, localeUtils, locale }) => (
  <CaptionContainer>
    <Flex height='100%' alignItems='center' justifyContent='center'>
      <P bold textAlign='center' normalised color={colors.text}>
        {localeUtils.formatMonthTitle(date, locale)}
      </P>
    </Flex>
  </CaptionContainer>
)
Caption.propTypes = {
  date: PropTypes.instanceOf(Date).isRequired, // injected by react-day-picker,
  localeUtils: PropTypes.object.isRequired, // injected by react-day-picker
  locale: PropTypes.string
}

/* https://stackoverflow.com/questions/7638677/how-can-i-remove-the-gloss-on-a-select-element-in-safari-on-mac */
const webkitArrowDataUrl = 'data:image/svg+xml;base64,PHN2ZyBpZD0iTGF5ZXJfMSIgZGF0YS1uYW1lPSJMYXllciAxIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA0Ljk1IDEwIj48ZGVmcz48c3R5bGU+LmNscy0xe2ZpbGw6I2ZmZjt9LmNscy0ye2ZpbGw6IzQ0NDt9PC9zdHlsZT48L2RlZnM+PHRpdGxlPmFycm93czwvdGl0bGU+PHJlY3QgY2xhc3M9ImNscy0xIiB3aWR0aD0iNC45NSIgaGVpZ2h0PSIxMCIvPjxwb2x5Z29uIGNsYXNzPSJjbHMtMiIgcG9pbnRzPSIxLjQxIDQuNjcgMi40OCAzLjE4IDMuNTQgNC42NyAxLjQxIDQuNjciLz48cG9seWdvbiBjbGFzcz0iY2xzLTIiIHBvaW50cz0iMy41NCA1LjMzIDIuNDggNi44MiAxLjQxIDUuMzMgMy41NCA1LjMzIi8+PC9zdmc+'

const StyledSelect = styled.select`
  border: none;
  background: none;
  outline: none;
  font-weight: 600;
  font-size: 1em;
  cursor: pointer;
  &::-ms-expand {
    display: none;
  }
  /* stylelint-disable property-no-vendor-prefix */
  -moz-appearance: none;
  -webkit-appearance: none;
  /* stylelint-enable */
  background: url(${webkitArrowDataUrl}) no-repeat 100% 50%;
  padding-right: ${padding[paddingSizes.medium]};
  margin: 0 ${margin[marginSizes.medium]};
`

const Dropdown = ({ value, onChange, options, valueFormatter = e => e, ...props }) => (
  <StyledSelect onChange={onChange} value={value} {...props}>
    {options.map((option) => <option key={option} value={valueFormatter(option)}>{option}</option>)}
  </StyledSelect>
)
Dropdown.propTypes = {
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  valueFormatter: PropTypes.func
}

class MonthYearDropdownClass extends React.PureComponent {
  static propTypes = {
    onChange: PropTypes.func,
    date: PropTypes.instanceOf(Date).isRequired, // injected by react-day-picker
    fromMonth: PropTypes.instanceOf(Date).isRequired,
    toMonth: PropTypes.instanceOf(Date).isRequired
  }

  static defaultProps = {
    onChange: () => {}
  }

  handleChange = (field) => (e) => {
    const newValue = e.target.value
    const { date, fromMonth, toMonth, onChange } = this.props
    let dateToUpdate = (field === 'month' && new Date(date.getFullYear(), newValue)) || new Date(newValue, date.getMonth())
    if (dateToUpdate < fromMonth) {
      dateToUpdate = new Date(dateToUpdate.getFullYear(), fromMonth.getMonth())
    } else if (dateToUpdate > toMonth) {
      dateToUpdate = new Date(dateToUpdate.getFullYear(), toMonth.getMonth())
    }
    onChange(dateToUpdate)
  }

  getMonthOptions = (allMonthsInOneYear) => {
    const { fromMonth, toMonth, date } = this.props
    const year = date.getFullYear()
    if (year === fromMonth.getFullYear()) {
      return allMonthsInOneYear.slice(fromMonth.getMonth(), 12)
    } else if (year === toMonth.getFullYear()) {
      return allMonthsInOneYear.slice(0, toMonth.getMonth() + 1)
    }
    return allMonthsInOneYear
  }

  getYearOptions = () => {
    const { fromMonth, toMonth } = this.props
    const years = []
    for (let i = fromMonth.getFullYear(); i <= toMonth.getFullYear(); i += 1) {
      years.push(i)
    }
    return years
  }

  render () {
    const { t, date } = this.props
    const allMonthsInOneYear = getTranslatedMonths(t)
    const months = this.getMonthOptions(allMonthsInOneYear)
    const years = this.getYearOptions()

    return (
      <CaptionContainer>
        <Flex height='100%' justifyContent='center'>
          <Dropdown
            data-testid='datePicker.monthSelect'
            value={date.getMonth()}
            options={months}
            onChange={this.handleChange('month')}
            valueFormatter={(month) => allMonthsInOneYear.indexOf(month)}
          />
          <Dropdown
            data-testid='datePicker.yearSelect'
            value={date.getFullYear()}
            options={years}
            onChange={this.handleChange('year')}
          />
        </Flex>
      </CaptionContainer>
    )
  }
}

export const MonthYearDropdown = Component()(MonthYearDropdownClass)

export default Component()(Caption)
